import { useState } from 'react';
import { genreOptions, regionOptions, yearOptions } from '@/mocks/titles';
import type { FilterState } from '@/utils/titles';

interface FilterPanelProps {
  filters: FilterState;
  onChange: (next: FilterState) => void;
  showStatus?: boolean;
}

interface Option {
  value: string;
  label: string;
}

const toOptions = (values: (string | number)[]): Option[] =>
  values.map((value) => ({ value: String(value), label: String(value) }));

const sortOptions: Option[] = [
  { value: 'default', label: '默认排序' },
  { value: 'rating', label: '评分最高' },
  { value: 'newest', label: '最近更新' },
  { value: 'year', label: '年份最新' },
];

const statusOptions: Option[] = [
  { value: 'ongoing', label: '连载中' },
  { value: 'completed', label: '已完结' },
];

interface ChipRowProps {
  label: string;
  options: Option[];
  value: string;
  accent?: boolean;
  onSelect: (value: string) => void;
}

function ChipRow({ label, options, value, accent = false, onSelect }: ChipRowProps) {
  const chip = (active: boolean) =>
    `px-3 py-1.5 rounded-full text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap border ${
      active
        ? accent
          ? 'bg-accent-500 text-primary-950 border-accent-500'
          : 'bg-primary-500 text-background-50 border-primary-500'
        : 'bg-background-50 text-foreground-700 border-background-300 hover:border-primary-300 hover:text-primary-700'
    }`;

  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1.5 sm:gap-4 py-2.5 border-b border-background-200 last:border-b-0">
      <span className="shrink-0 w-14 text-sm font-medium text-foreground-700 pt-1">{label}</span>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={chip(value === '')} onClick={() => onSelect('')}>
          全部
        </button>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={chip(value === option.value)}
            onClick={() => onSelect(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterPanel({ filters, onChange, showStatus = false }: FilterPanelProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const update = (patch: Partial<FilterState>) => onChange({ ...filters, ...patch });
  const activeCount = [filters.genre, filters.region, filters.year, filters.status].filter(Boolean).length;

  const content = (
    <div className="w-full">
      <ChipRow label="类型" options={toOptions(genreOptions)} value={filters.genre} onSelect={(v) => update({ genre: v })} />
      <ChipRow label="地区" options={toOptions(regionOptions)} value={filters.region} onSelect={(v) => update({ region: v })} />
      <ChipRow label="年份" options={toOptions(yearOptions)} value={filters.year} onSelect={(v) => update({ year: v })} />
      {showStatus && (
        <ChipRow label="状态" options={statusOptions} value={filters.status} onSelect={(v) => update({ status: v })} />
      )}
      <ChipRow label="排序" options={sortOptions} value={filters.sort} accent onSelect={(v) => update({ sort: v as FilterState['sort'] })} />
    </div>
  );

  const resetFilters = () => onChange({ ...filters, genre: '', region: '', year: '', status: '' });

  return (
    <>
      <div className="hidden md:block rounded-lg bg-background-100 border border-background-200 px-5 py-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-heading font-semibold text-foreground-900 flex items-center gap-2">
            <i className="ri-filter-3-line text-primary-600"></i>筛选条件
          </span>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={resetFilters}
              className="text-xs text-foreground-600 hover:text-accent-600 flex items-center gap-1 cursor-pointer"
            >
              <i className="ri-refresh-line"></i>重置筛选
            </button>
          )}
        </div>
        {content}
      </div>

      <div className="md:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="w-full h-11 rounded-md bg-background-100 border border-background-200 flex items-center justify-center gap-2 text-sm font-medium text-foreground-800 cursor-pointer"
        >
          <i className="ri-filter-3-line text-primary-600"></i>
          筛选与排序
          {activeCount > 0 && (
            <span className="ml-1 min-w-5 h-5 px-1.5 rounded-full bg-accent-500 text-primary-950 text-xs flex items-center justify-center">
              {activeCount}
            </span>
          )}
        </button>

        {drawerOpen && (
          <div className="fixed inset-0 z-[60] flex items-end">
            <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setDrawerOpen(false)}></div>
            <div className="relative w-full max-h-[80vh] overflow-y-auto bg-background-50 rounded-t-2xl p-4 animate-fade-in-up">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading font-semibold text-foreground-950">筛选与排序</h3>
                <button
                  type="button"
                  aria-label="关闭"
                  onClick={() => setDrawerOpen(false)}
                  className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              {content}
              <div className="flex gap-3 mt-4">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="flex-1 h-11 rounded-md border border-background-300 text-foreground-700 text-sm font-medium cursor-pointer whitespace-nowrap"
                >
                  重置
                </button>
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  className="flex-1 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
                >
                  查看结果
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}